import { NetworkType } from '@layerswap/widget/types';
import { useEvmStore } from './service/evmStore';
const RPC_PROBE_TIMEOUT_MS = 8000;
// Wallet in-app browsers (Rainbow, MetaMask mobile, etc.) and some WalletConnect
// sessions don't proxy arbitrary read methods like `eth_getBlockByNumber`; they
// reject with an "unsupported method" / "unauthorized" error that says nothing
// about RPC health. Treating those as unhealthy would show the (non-actionable)
// "add RPC" prompt inside wallet browsers even when the RPC is perfectly fine.
const UNSUPPORTED_METHOD_CODES = [
    4200, // EIP-1193: Unsupported Method
    4100, // EIP-1193: Unauthorized
    -32601, // JSON-RPC: Method not found
    -32004, // JSON-RPC: Method not supported
];
const UNSUPPORTED_METHOD_MESSAGES = [
    'not supported',
    'method not found',
    'unsupported method',
    'unauthorized',
    'does not exist',
    'not available',
];
function isMethodUnsupportedError(e) {
    if (UNSUPPORTED_METHOD_CODES.includes(e?.code))
        return true;
    const msg = String(e?.message ?? '').toLowerCase();
    return UNSUPPORTED_METHOD_MESSAGES.some((m) => msg.includes(m));
}
function withTimeout(promise, ms, message) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
const INITIAL_SNAPSHOT = Object.freeze({
    health: { status: undefined },
    isSuggestingRpc: false,
});
function createStore() {
    let snapshot = INITIAL_SNAPSHOT;
    const listeners = new Set();
    let lastConnectorId;
    let lastIsConnected = false;
    let unsubEvm = null;
    const setSnapshot = (next) => {
        snapshot = { ...snapshot, ...next };
        listeners.forEach(l => l());
    };
    const getActiveConnector = () => {
        const state = useEvmStore.getState();
        const isConnected = !!state.wagmiAccount.address;
        const connector = state.allConnectors.find(c => c.id === state.wagmiAccount.connectorId);
        return { connector, isConnected, chainId: state.wagmiAccount.chainId };
    };
    const check = async () => {
        const { connector, isConnected } = getActiveConnector();
        if (!connector || !isConnected)
            return;
        try {
            const provider = (await connector.getProvider());
            if (!provider || typeof provider.request !== 'function')
                return;
            const start = performance.now();
            const latestBlock = await withTimeout(provider.request({
                method: 'eth_getBlockByNumber',
                params: ['latest', false],
            }), RPC_PROBE_TIMEOUT_MS, 'Wallet RPC timed out');
            const latencyMs = performance.now() - start;
            const tsHex = latestBlock?.timestamp;
            const blockAgeSec = tsHex != null
                ? Date.now() / 1000 - parseInt(tsHex, 16)
                : Number.POSITIVE_INFINITY;
            const tooSlow = latencyMs > 2000;
            const tooStale = blockAgeSec > 60;
            if (tooSlow || tooStale) {
                let reason = '';
                if (tooSlow)
                    reason += `Wallet RPC is slow (${latencyMs.toFixed(0)}ms). `;
                if (tooStale)
                    reason += `Latest block is stale (${blockAgeSec.toFixed(0)}s old).`;
                setSnapshot({ health: { status: 'unhealthy', reason: reason.trim() } });
                return;
            }
            setSnapshot({ health: { status: 'healthy', latencyMs, blockAgeSec } });
        }
        catch (e) {
            // A wallet declining to serve the read method isn't an RPC health signal —
            // leave status "unknown" so we don't prompt the user to add an RPC.
            if (isMethodUnsupportedError(e)) {
                setSnapshot({ health: { status: undefined } });
                return;
            }
            const msg = e?.message || 'Unknown error from wallet RPC';
            setSnapshot({ health: { status: 'unhealthy', reason: msg } });
        }
    };
    const suggestRpc = async (params) => {
        const { connector, isConnected } = getActiveConnector();
        if (!connector || !isConnected) {
            return { success: false, error: 'Wallet not connected' };
        }
        setSnapshot({ isSuggestingRpc: true });
        try {
            const provider = (await connector.getProvider());
            if (!provider || typeof provider.request !== 'function') {
                return { success: false, error: 'No wallet provider available' };
            }
            await provider.request({
                method: 'wallet_addEthereumChain',
                params: [params],
            });
            await check();
            return { success: true };
        }
        catch (e) {
            return { success: false, error: e?.message || 'Failed to update wallet RPC' };
        }
        finally {
            setSnapshot({ isSuggestingRpc: false });
        }
    };
    const suggestRpcForCurrentChain = async (rpcUrl, chainDetails) => {
        const { chainId } = getActiveConnector();
        if (!chainId)
            return { success: false, error: 'No chain connected' };
        return suggestRpc({
            chainId: `0x${chainId.toString(16)}`,
            rpcUrls: [rpcUrl],
            ...chainDetails,
        });
    };
    // Auto-check when the active connector or connectedness changes. The
    // upstream subscription only lives while someone is listening (first
    // subscriber starts it, last unsubscriber stops it), so consumer
    // mount/unmount cycles — including StrictMode's — can't leave the store
    // dead. The evm-store listener only reacts to changes, so on start we
    // also catch up: if a wallet is already connected, check right away.
    const startAutoCheck = () => {
        unsubEvm = useEvmStore.subscribe(() => {
            const { connector, isConnected } = getActiveConnector();
            const connectorId = connector?.id;
            if (connectorId === lastConnectorId && isConnected === lastIsConnected)
                return;
            lastConnectorId = connectorId;
            lastIsConnected = isConnected;
            if (connector && isConnected)
                void check();
        });
        const { connector, isConnected } = getActiveConnector();
        lastConnectorId = connector?.id;
        lastIsConnected = isConnected;
        if (connector && isConnected)
            void check();
    };
    const stopAutoCheck = () => {
        unsubEvm?.();
        unsubEvm = null;
    };
    return {
        subscribe(listener) {
            if (listeners.size === 0)
                startAutoCheck();
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
                if (listeners.size === 0)
                    stopAutoCheck();
            };
        },
        getSnapshot() {
            return snapshot;
        },
        checkManually: check,
        suggestRpc,
        suggestRpcForCurrentChain,
    };
}
export class EVMRpcHealthCheckProvider {
    constructor() {
        this._store = null;
    }
    supportsNetwork(network) {
        return network.type === NetworkType.EVM;
    }
    createStore() {
        if (!this._store)
            this._store = createStore();
        return this._store;
    }
}
